import { ApolloQueryResult } from '@apollo/client';
import { useCallback } from 'react';

import useCloneCampaignByID from '../../apis/graphql/campaigns/useCloneCampaignByID';
import useDeleteCampaign from '../../apis/graphql/campaigns/useDeleteCampaign';
import useDeleteDraftLineItem from '../../apis/graphql/campaigns/useDeleteDraftLineItem';
import useQuickActionLineItemByID from '../../apis/graphql/campaigns/useQuickActionLineItemByID';
import {
  CreativeStatus,
  GetCampaignByUuidQuery,
  GetCampaignsQuery,
  GetCampaignsWithExtraDataQuery,
  GetLineItemsByCampaignIdQuery,
  LineItemByIdQuery,
  LineItemQuickAction,
  LineItemStatus,
} from '../../apis/graphql/types';
import admanToast from '../../components/Alert/util/admanToast';
import { CampaignsTable } from '../../pages/Campaigns/CampaignsTable/constants';
import {
  CANCELLABLE_STATUSES,
  PAUSABLE_STATUSES,
  RESUMABLE_STATUSES,
  SUSPENDABLE_STATUSES,
} from '../../pages/Campaigns/constants';
import { getItemTypeName, getPluralEnding } from '../../pages/Campaigns/utils';
import handleError from '../../utils/handleError';

type HandleQuickActionProps = {
  itemId: string;
  isCampaign?: boolean;
  callback?: () => Promise<
    ApolloQueryResult<GetCampaignByUuidQuery | GetCampaignsQuery | GetLineItemsByCampaignIdQuery | LineItemByIdQuery>
  >;
};

export type HandleQuickActionWithLineItemProps = {
  itemId: string;
  isCampaign?: boolean;
  lineItems?: CampaignsTable['subRows'];
  statusToExclude?: string[];
  callback?: () => Promise<
    ApolloQueryResult<
      | GetCampaignByUuidQuery
      | GetCampaignsQuery
      | GetLineItemsByCampaignIdQuery
      | LineItemByIdQuery
      | GetCampaignsWithExtraDataQuery
    >
  >;
};

export type UseHandleQuickActions = {
  deleteActionLoading: boolean;
  duplicateLoading: boolean;
  quickActionsLoading: boolean;
  handleCancelQuickAction: (props: HandleQuickActionWithLineItemProps) => Promise<void>;
  handleDeleteQuickAction: (props: HandleQuickActionProps) => Promise<void>;
  handleDuplicateQuickAction: (props: HandleQuickActionProps) => Promise<string | void>;
  handlePauseQuickAction: (props: HandleQuickActionWithLineItemProps) => Promise<void>;
  handleResumeQuickAction: (props: HandleQuickActionWithLineItemProps) => Promise<void>;
  handleSuspendQuickAction: (props: HandleQuickActionWithLineItemProps) => Promise<void>;
};

const useHandleQuickActions = (): UseHandleQuickActions => {
  const { fetchCloneCampaignByID, loading: duplicateLoading } = useCloneCampaignByID();
  const { handleQuickActionLineItemByID, loading: quickActionsLoading } = useQuickActionLineItemByID();
  const { handleDeleteCampaign, loading: deleteCampaignLoading } = useDeleteCampaign();
  const { handleDeleteDraftLineItem, loading: deleteLineItemLoading } = useDeleteDraftLineItem();

  const handleDeleteQuickAction = useCallback(
    async ({ itemId, callback, isCampaign = false }: HandleQuickActionProps) => {
      try {
        if (isCampaign) {
          await handleDeleteCampaign(itemId);
        } else {
          await handleDeleteDraftLineItem(itemId);
        }

        admanToast.success(`${getItemTypeName(isCampaign)} has been deleted.`);
        await callback?.();
      } catch (error) {
        handleError(error as Error);
      }
    },
    [handleDeleteCampaign, handleDeleteDraftLineItem]
  );

  const handleDuplicateQuickAction = useCallback(
    async ({ itemId, callback, isCampaign = false }: HandleQuickActionProps) => {
      try {
        const data = await (isCampaign
          ? fetchCloneCampaignByID(itemId)
          : handleQuickActionLineItemByID(itemId, LineItemQuickAction.Clone));

        if (data) {
          await callback?.();

          admanToast.success(`${getItemTypeName(isCampaign)} has been duplicated`);

          return data.id;
        }
      } catch (error) {
        console.error(error);
        handleError(error as Error);
      }
    },
    [fetchCloneCampaignByID, handleQuickActionLineItemByID]
  );

  const handlePauseQuickAction = useCallback(
    async ({ itemId, callback, isCampaign = false, lineItems }: HandleQuickActionWithLineItemProps) => {
      try {
        if (isCampaign && !!lineItems) {
          await Promise.all(
            lineItems
              .filter((item) => !!item.status && PAUSABLE_STATUSES.includes(item.status))
              .map((item) => item.id && handleQuickActionLineItemByID(item.id, LineItemQuickAction.Pause))
          )
            .then(() => {
              callback?.();
              admanToast.success(`Line Items have been paused`);
            })
            .catch(() => {
              admanToast.error(`Unable to pause Line Items`);
            });
        } else if (!isCampaign) {
          await handleQuickActionLineItemByID(itemId, LineItemQuickAction.Pause);

          await callback?.();
          admanToast.success(`Line Item has been paused`);
        }
      } catch (error) {
        admanToast.error(`Unable to pause Line Item${getPluralEnding(isCampaign)}`);
        console.error(error);
      }
    },
    [handleQuickActionLineItemByID]
  );

  const handleCancelQuickAction = useCallback(
    async ({
      itemId,
      callback,
      isCampaign = false,
      lineItems,
      statusToExclude = CANCELLABLE_STATUSES,
    }: HandleQuickActionWithLineItemProps) => {
      try {
        if (isCampaign && !!lineItems) {
          await Promise.all(
            lineItems
              .filter((item) => !!item.status && statusToExclude.includes(item.status))
              .map((item) => item.id && handleQuickActionLineItemByID(item.id, LineItemQuickAction.Cancel))
          )
            .then(() => {
              callback?.();
              admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been canceled`);
            })
            .catch(() => {
              admanToast.error(`Unable to cancel Line Item${getPluralEnding(isCampaign)}`);
            });
        } else if (!isCampaign) {
          await handleQuickActionLineItemByID(itemId, LineItemQuickAction.Cancel);

          await callback?.();
          admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been canceled`);
        }
      } catch (error) {
        admanToast.error(`Unable to cancel Line Item${getPluralEnding(isCampaign)}`);
        console.error(error);
      }
    },
    [handleQuickActionLineItemByID]
  );

  const handleSuspendQuickAction = useCallback(
    async ({ itemId, callback, isCampaign = false, lineItems }: HandleQuickActionWithLineItemProps) => {
      try {
        if (isCampaign && !!lineItems) {
          await Promise.all(
            lineItems
              .filter((item) => !!item.status && SUSPENDABLE_STATUSES.includes(item.status))
              .map((item) => item.id && handleQuickActionLineItemByID(item.id, LineItemQuickAction.Suspend))
          )
            .then(() => {
              callback?.();
              admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been suspended`);
            })
            .catch(() => {
              admanToast.error(`Unable to suspend Line Item${getPluralEnding(isCampaign)}`);
            });
        } else if (!isCampaign) {
          await handleQuickActionLineItemByID(itemId, LineItemQuickAction.Suspend);

          await callback?.();
          admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been suspended`);
        }
      } catch (error) {
        admanToast.error(`Unable to suspend Line Item${getPluralEnding(isCampaign)}`);
        console.error(error);
      }
    },
    [handleQuickActionLineItemByID]
  );

  const handleResumeQuickAction = useCallback(
    async ({
      itemId,
      callback,
      isCampaign = false,
      lineItems,
      statusToExclude = RESUMABLE_STATUSES,
    }: HandleQuickActionWithLineItemProps) => {
      try {
        if (isCampaign && !!lineItems) {
          await Promise.all(
            lineItems
              .filter(
                (item) =>
                  !!item.status &&
                  (item.status === LineItemStatus.Suspended ||
                    item.adStatus === CreativeStatus.Published ||
                    item.adStatus === CreativeStatus.Vast) &&
                  statusToExclude.includes(item.status)
              )
              .map((item) => item.id && handleQuickActionLineItemByID(item.id, LineItemQuickAction.Resume))
          )
            .then(() => {
              callback?.();
              admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been resumed`);
            })
            .catch(() => {
              admanToast.error(`Unable to resume Line Item${getPluralEnding(isCampaign)}`);
            });
        } else if (!isCampaign) {
          await handleQuickActionLineItemByID(itemId, LineItemQuickAction.Resume);

          await callback?.();
          admanToast.success(`Line Item${getPluralEnding(isCampaign)} has been resumed`);
        }
      } catch (error) {
        admanToast.error(`Unable to resume Line Item${getPluralEnding(isCampaign)}`);
        console.error(error);
      }
    },
    [handleQuickActionLineItemByID]
  );

  return {
    quickActionsLoading,
    duplicateLoading,
    deleteActionLoading: deleteLineItemLoading || deleteCampaignLoading,
    handleCancelQuickAction,
    handleDeleteQuickAction,
    handleDuplicateQuickAction,
    handlePauseQuickAction,
    handleResumeQuickAction,
    handleSuspendQuickAction,
  };
};

export default useHandleQuickActions;
